import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { CompanyService } from '../CompanyService';
import { ErrorToast } from '../../../utils/toasters';

interface IGroup {
    id: number;
    name: string;
    nameDe?: string;
    isMultiple: boolean;
    isActive: boolean;
    supplier?: { id: number; displayName: string };
}

interface IEditGroupModal {
    show: boolean;
    clientId: number;
    group: IGroup | null;
    onHide: () => void;
    onUpdated: (group: IGroup) => void;
}

export function EditGroupModal({ show, clientId, group, onHide, onUpdated }: IEditGroupModal) {
    const [form, setForm] = useState({ name: '', nameDe: '', supplierId: '', isMultiple: false, isActive: true });
    const [suppliers, setSuppliers] = useState<{ id: number; displayName: string }[]>([]);

    useEffect(() => {
        if (show) {
            CompanyService.getClientSuppliers(clientId)
                .then(data => setSuppliers(data.data ?? data))
                .catch(err => ErrorToast(err));
        }
    }, [show]);
    useEffect(() => {
        if (group) {
            setForm({
                name: group.name,
                nameDe: group.nameDe ?? '',
                supplierId: group.supplier?.id ? String(group.supplier.id) : '',
                isMultiple: group.isMultiple,
                isActive: group.isActive,
            });
        }
    }, [group]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = () => {
        if (!group) return;
        const body = {
            name: form.name,
            ...(form.nameDe && { nameDe: form.nameDe }),
            ...(form.supplierId && { supplierId: Number(form.supplierId) }),
            isMultiple: form.isMultiple,
            isActive: form.isActive,
        };
        CompanyService.updateGroup(group.id, body)
            .then(data => {
                onUpdated(data);
                onHide();
            })
            .catch(err => ErrorToast(err));
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit group</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label>
                        Name EN <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control name="name" value={form.name} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Name DE</Form.Label>
                    <Form.Control name="nameDe" value={form.nameDe} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Supplier</Form.Label>
                    <Form.Select
                        name="supplierId"
                        value={form.supplierId}
                        onChange={e => setForm(prev => ({ ...prev, supplierId: e.target.value }))}
                    >
                        <option value="">— None —</option>
                        {suppliers.map(s => (
                            <option key={s.id} value={s.id}>
                                {s.displayName}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Check
                    className="mb-2"
                    type="checkbox"
                    name="isMultiple"
                    label="Multiple select"
                    checked={form.isMultiple}
                    onChange={handleChange}
                />
                <Form.Check
                    type="checkbox"
                    name="isActive"
                    label="Active"
                    checked={form.isActive}
                    onChange={handleChange}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!form.name}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
